import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getCorporateEmployeeByQuery } from '../api/endpoints/corporateEmployee';
import type { CorporateEmployee } from '../api/types/corporateEmployee';

function optionLabel(option: CorporateEmployee): string {
  const fullName = [option.name, option.surName].filter(Boolean).join(' ');
  return option.registrationNumber ? `${fullName} (${option.registrationNumber})` : fullName;
}

/** Personel (corporate employee) autocomplete backed by
 *  `CorporateEmployee/GetCorporateEmployeeByQuery` (min 3 chars, debounced via staleTime) —
 *  same search-as-you-type pattern as `AirportAutocomplete`. Selected employee's
 *  `employeeID` is what callers should slot into the search request. */
export function PersonnelAutocomplete({
  label = 'Personel',
  value,
  onChange,
}: {
  label?: string;
  value: CorporateEmployee | null;
  onChange: (value: CorporateEmployee | null) => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const trimmed = inputValue.trim();

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['corporateEmployee', 'query', trimmed],
    queryFn: () =>
      getCorporateEmployeeByQuery({ name: trimmed }).then((res) => res.corporateEmployeeList ?? []),
    enabled: trimmed.length >= 3,
    staleTime: 60_000,
  });

  return (
    <Autocomplete
      sx={{ minWidth: 240, flex: 1 }}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, val) => option.employeeID === val.employeeID}
      loading={isFetching}
      noOptionsText={trimmed.length < 3 ? 'Aramak için en az 3 karakter girin' : 'Personel bulunamadı'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {isFetching && <CircularProgress color="inherit" size={16} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
